import React, { useEffect, useState } from 'react';
import {
  CloudOutlined,
  FileOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  FolderOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import api from '../api/client';

// Хост Nextcloud (для распознавания ссылок в тексте)
export const NC_HOST = 'opp.spzvg.ru';

// Кэш метаданных превью (на сессию приложения)
const cache = new Map();

export function isNcUrl(url) {
  try {
    return new URL(url).hostname.includes(NC_HOST);
  } catch {
    return false;
  }
}

function fmtSize(bytes) {
  if (!bytes && bytes !== 0) return null;
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} ГБ`;
}

function iconFor(info) {
  const mime = info?.mime || '';
  const name = info?.name || '';
  if (info?.kind === 'folder') return <FolderOutlined style={{ fontSize: 28, color: '#fbbf24' }} />;
  if (mime.startsWith('image/') || /\.(png|jpe?g|gif|webp|svg|bmp|heic)$/i.test(name))
    return <FileImageOutlined style={{ fontSize: 28, color: '#3b82f6' }} />;
  if (mime.includes('pdf') || /\.pdf$/i.test(name))
    return <FilePdfOutlined style={{ fontSize: 28, color: '#ef4444' }} />;
  return <FileOutlined style={{ fontSize: 28, color: '#64748b' }} />;
}

function shortPath(info) {
  const p = info?.path || '';
  if (!p) return null;
  const parts = p.split('/').filter(Boolean);
  // /ТЕРРА-СУП/projects/<проект>/<задача> → <проект> / <задача>
  if (parts[0] === 'ТЕРРА-СУП') parts.shift();
  const tail = parts.slice(-3).join(' / ');
  return tail.length < parts.join(' / ').length ? `… ${tail}` : tail;
}

/**
 * Превью ссылки Nextcloud в тексте (чат/комментарии/лента/описания).
 * block=true — карточка (ссылка на своей строке), иначе — компактный чип.
 * Метаданные: GET /api/v1/files/nc-link-info?url=… (кэш в памяти).
 */
export default function NcLinkPreview({ url, block = false }) {
  const [info, setInfo] = useState(cache.get(url) || null);
  const [thumbError, setThumbError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (cache.has(url)) {
      setInfo(cache.get(url));
      return () => {};
    }
    setInfo(null);
    api
      .get('/files/nc-link-info', { params: { url } })
      .then(({ data }) => {
        if (cancelled) return;
        cache.set(url, data);
        setInfo(data);
      })
      .catch(() => {
        if (!cancelled) {
          const fallback = { supported: false };
          cache.set(url, fallback);
          setInfo(fallback);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [url]);

  if (!info || !info.supported) {
    // не смогли разобрать — обычная ссылка
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
        {url}
      </a>
    );
  }

  const open = (e) => {
    e.stopPropagation();
    window.open(url, '_blank', 'noopener');
  };

  const thumb = info.thumb && !thumbError ? (
    <img
      src={info.thumb}
      alt={info.name || ''}
      onError={() => setThumbError(true)}
      style={{ objectFit: 'cover', width: '100%', height: '100%' }}
    />
  ) : null;

  const title = info.name || info.path?.split('/').filter(Boolean).pop() || 'Файл в Nextcloud';
  const size = fmtSize(info.size);
  const where = shortPath(info);

  if (!block) {
    // компактный чип внутри текста
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          padding: '1px 8px 1px 6px',
          margin: '0 2px',
          borderRadius: 999,
          border: '1px solid #c7d2fe',
          background: '#eef2ff',
          color: '#4338ca',
          textDecoration: 'none',
          fontSize: '0.95em',
          maxWidth: 320,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          verticalAlign: 'bottom',
        }}
        title={where ? `${where} · ${title}` : title}
      >
        {iconFor({ ...info, kind: info.kind })}
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</span>
      </a>
    );
  }

  // полная карточка (ссылка на своей строке)
  return (
    <div
      onClick={open}
      style={{
        display: 'flex',
        alignItems: 'stretch',
        gap: 12,
        margin: '6px 0',
        padding: 10,
        borderRadius: 12,
        border: '1px solid #e2e8f0',
        background: '#f8fafc',
        cursor: 'pointer',
        maxWidth: 520,
      }}
      title="Открыть в Nextcloud"
    >
      <div
        style={{
          width: 72,
          height: 72,
          borderRadius: 8,
          overflow: 'hidden',
          flexShrink: 0,
          background: thumb ? 'transparent' : '#eef2ff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {thumb || iconFor(info)}
      </div>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <div
          style={{
            fontWeight: 600,
            color: '#0f172a',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {title}
        </div>
        {where && (
          <div style={{ color: '#64748b', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {where}
          </div>
        )}
        <div style={{ color: '#94a3b8', fontSize: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
          {size && <span>{size}</span>}
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#3b82f6' }}>
            <CloudOutlined /> Nextcloud
          </span>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', color: '#94a3b8', flexShrink: 0 }}>
        <LinkOutlined />
      </div>
    </div>
  );
}
